import React from 'react';
import {
  FaComments, FaChevronUp, FaChevronDown, FaPaperclip,
  FaTrash, FaDownload, FaImage, FaTimes, FaPaperPlane,
} from 'react-icons/fa';
import { JobComment, JobAttachment } from '../services/PlantafelService';
import styles from './PlantafelSidePanel.module.css';

interface SidePanelCommentsProps {
  isJobShift: boolean;
  crmTaskId: number | undefined;
  hasTaskContext: boolean;
  showComments: boolean;
  setShowComments: (show: boolean) => void;
  jobComments: JobComment[];
  jobAttachments: JobAttachment[];
  commentsLoading: boolean;
  attachmentsLoading: boolean;
  newCommentText: string;
  setNewCommentText: (text: string) => void;
  pendingFiles: File[];
  sendingComment: boolean;
  deletingCommentId: number | null;
  deletingAttachmentId: number | null;
  attachingToCommentId: number | null;
  handleSendComment: () => void;
  handleAddPendingFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemovePendingFile: (index: number) => void;
  handleAttachToComment: (commentId: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteComment: (commentId: number) => void;
  handleDeleteAttachment: (attachmentId: number) => void;
}

export function SidePanelComments({
  isJobShift, crmTaskId, hasTaskContext, showComments, setShowComments,
  jobComments, jobAttachments, commentsLoading, attachmentsLoading,
  newCommentText, setNewCommentText, pendingFiles, sendingComment,
  deletingCommentId, deletingAttachmentId, attachingToCommentId,
  handleSendComment, handleAddPendingFile, handleRemovePendingFile,
  handleAttachToComment, handleDeleteComment, handleDeleteAttachment,
}: SidePanelCommentsProps): React.JSX.Element | null {
  if (isJobShift && crmTaskId) {
    const jobLevelComments = jobComments.filter(c => !c.checklist_item_id);
    const jobLevelAttachments = jobAttachments.filter(a => !a.checklist_item_id);

    return (
      <div data-testid="job-comments-section" style={{ marginBottom: '16px' }}>
        <button onClick={() => setShowComments(!showComments)} className={styles.commentsToggle}>
          <span className={styles.commentsToggleLabel}>
            <FaComments size={12} />
            Kommentare {jobLevelComments.length > 0 || jobLevelAttachments.length > 0 ? `(${jobLevelComments.length})` : ''}
            {jobLevelAttachments.length > 0 && (
              <span className={styles.attachmentCountLabel}>
                <FaPaperclip size={10} />{jobLevelAttachments.length}
              </span>
            )}
          </span>
          {showComments ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </button>

        {showComments && (
          <div className={styles.commentsContent}>
            {commentsLoading || attachmentsLoading ? (
              <p style={{ color: '#888', fontSize: '12px', margin: 0 }}>Laden...</p>
            ) : (
              <>
                {renderJobLevelComments(jobLevelComments, jobLevelAttachments)}

                {pendingFiles.length > 0 && (
                  <div className={styles.pendingFilesAreaJob}>
                    <p className={styles.pendingFilesLabel}>{pendingFiles.length} Datei(en) zum Anhängen:</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {pendingFiles.map((file, idx) => (
                        <div key={idx} className={styles.pendingFileChipJob}>
                          {file.type.startsWith('image/') ? <FaImage size={10} style={{ color: '#666' }} /> : <FaPaperclip size={10} style={{ color: '#666' }} />}
                          <span className={styles.pendingFileNameJob}>{file.name}</span>
                          <button onClick={() => handleRemovePendingFile(idx)} className={styles.pendingFileRemoveJob} title="Entfernen">
                            <FaTimes size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <textarea
                      data-testid="job-comment-input"
                      value={newCommentText}
                      onChange={e => setNewCommentText(e.target.value)}
                      placeholder="Kommentar schreiben..."
                      rows={2}
                      className={styles.jobCommentTextarea}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey && (newCommentText.trim() || pendingFiles.length > 0)) {
                          e.preventDefault();
                          handleSendComment();
                        }
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <label
                      data-testid="job-attachment-upload"
                      className={`${styles.jobUploadLabel} ${sendingComment ? styles.jobUploadLabelDisabled : styles.jobUploadLabelEnabled}`}
                    >
                      <FaPaperclip size={10} />Datei
                      <input type="file" onChange={handleAddPendingFile} disabled={sendingComment} style={{ display: 'none' }} multiple />
                    </label>
                    <label
                      data-testid="job-photo-upload"
                      className={`${styles.jobUploadLabel} ${sendingComment ? styles.jobUploadLabelDisabled : styles.jobUploadLabelEnabled}`}
                    >
                      <FaImage size={10} />Foto
                      <input type="file" accept="image/*" onChange={handleAddPendingFile} disabled={sendingComment} style={{ display: 'none' }} multiple />
                    </label>
                    <button
                      data-testid="job-comment-send"
                      onClick={handleSendComment}
                      disabled={sendingComment || (!newCommentText.trim() && pendingFiles.length === 0)}
                      className={styles.jobSendButton}
                      style={{
                        background: sendingComment || (!newCommentText.trim() && pendingFiles.length === 0) ? '#ccc' : '#021a4a',
                        cursor: sendingComment || (!newCommentText.trim() && pendingFiles.length === 0) ? 'not-allowed' : 'pointer',
                      }}
                      title="Senden"
                    >
                      <FaPaperPlane size={12} />
                      {sendingComment ? 'Sendet...' : 'Senden'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  if (hasTaskContext) {
    return (
      <div className={styles.commentsOnlyForJobs}>
        <p className={styles.commentsOnlyForJobsText}>
          <FaComments size={12} style={{ marginRight: '6px' }} />
          Kommentare nur für Jobs verfügbar
        </p>
      </div>
    );
  }

  return null;

  function renderJobLevelComments(comments: JobComment[], attachments: JobAttachment[]) {
    if (comments.length === 0 && attachments.filter(a => !a.comment_id).length === 0) return null;

    return (
      <div className={styles.commentsListScroll}>
        {comments.map(comment => {
          const commentAttachments = attachments.filter(a => a.comment_id === comment.id);
          const commentPhotos = commentAttachments.filter(a => a.content_type?.startsWith('image/'));
          const commentFiles = commentAttachments.filter(a => !a.content_type?.startsWith('image/'));
          return (
            <div key={comment.id} className={styles.commentBubbleJob}>
              <div className={styles.commentHeaderJob}>
                <span className={styles.commentAuthor}>{comment.author_name}</span>
                <div className={styles.commentMetaJob}>
                  <span className={styles.commentTimeJob}>
                    {new Date(comment.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <label
                    className={styles.attachToCommentLabel}
                    style={{ cursor: attachingToCommentId === comment.id ? 'wait' : 'pointer' }}
                    title="Foto/Datei anhängen"
                  >
                    {attachingToCommentId === comment.id ? (
                      <span style={{ fontSize: '9px' }}>...</span>
                    ) : (
                      <FaPaperclip size={10} />
                    )}
                    <input type="file" multiple onChange={(e) => handleAttachToComment(comment.id, e)} disabled={attachingToCommentId === comment.id} style={{ display: 'none' }} />
                  </label>
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    disabled={deletingCommentId === comment.id}
                    className={styles.commentDeleteBtn}
                    title="Löschen"
                  >
                    <FaTrash size={10} />
                  </button>
                </div>
              </div>
              {comment.text !== '(Dateianhang)' && <p className={styles.commentText}>{comment.text}</p>}
              {commentPhotos.length > 0 && (
                <div style={{ marginTop: '8px' }} className={styles.photoGridJob}>
                  {commentPhotos.map(photo => (
                    <div key={photo.id} className={styles.photoContainer}>
                      <a href={photo.file_url} target="_blank" rel="noopener noreferrer">
                        <img src={photo.thumbnail_url || photo.file_url} alt={photo.file_name} className={styles.photoCover} />
                      </a>
                      <button
                        onClick={() => handleDeleteAttachment(photo.id)}
                        disabled={deletingAttachmentId === photo.id}
                        className={styles.photoDeleteBtnJob}
                        title="Löschen"
                      >
                        <FaTrash size={7} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {commentFiles.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {commentFiles.map(file => (
                    <div key={file.id} className={styles.fileRowJob}>
                      <div className={styles.fileInfoJob}>
                        <FaPaperclip size={9} style={{ color: '#666', flexShrink: 0 }} />
                        <span className={styles.fileName}>{file.file_name}</span>
                        <span className={styles.fileSize}>{(file.file_size / 1024).toFixed(0)} KB</span>
                      </div>
                      <div className={styles.fileActions}>
                        <a href={file.file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#021a4a', padding: '2px' }} title="Herunterladen"><FaDownload size={9} /></a>
                        <button onClick={() => handleDeleteAttachment(file.id)} disabled={deletingAttachmentId === file.id} className={styles.commentDeleteBtn} title="Löschen"><FaTrash size={9} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {renderStandaloneAttachments(attachments)}
      </div>
    );
  }

  function renderStandaloneAttachments(attachments: JobAttachment[]) {
    const standaloneAttachments = attachments.filter(a => !a.comment_id);
    if (standaloneAttachments.length === 0) return null;
    const standalonePhotos = standaloneAttachments.filter(a => a.content_type?.startsWith('image/'));
    const standaloneFiles = standaloneAttachments.filter(a => !a.content_type?.startsWith('image/'));
    return (
      <div className={styles.standaloneFilesSectionJob}>
        <div className={styles.standaloneFilesHeader}>
          <FaPaperclip size={10} style={{ color: '#666' }} />
          <span className={styles.standaloneFilesLabel}>Dateien ohne Kommentar</span>
        </div>
        {standalonePhotos.length > 0 && (
          <div className={styles.photoGridJob} style={{ marginBottom: standaloneFiles.length > 0 ? '8px' : '0' }}>
            {standalonePhotos.map(photo => (
              <div key={photo.id} className={styles.photoContainer}>
                <a href={photo.file_url} target="_blank" rel="noopener noreferrer">
                  <img src={photo.thumbnail_url || photo.file_url} alt={photo.file_name} className={styles.photoCover} />
                </a>
                <button onClick={() => handleDeleteAttachment(photo.id)} disabled={deletingAttachmentId === photo.id} className={styles.photoDeleteBtnJob} title="Löschen"><FaTrash size={7} /></button>
              </div>
            ))}
          </div>
        )}
        {standaloneFiles.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {standaloneFiles.map(file => (
              <div key={file.id} className={styles.fileRowJob}>
                <div className={styles.fileInfoJob}>
                  <FaPaperclip size={9} style={{ color: '#666', flexShrink: 0 }} />
                  <span className={styles.fileName}>{file.file_name}</span>
                  <span className={styles.fileSize}>{(file.file_size / 1024).toFixed(0)} KB</span>
                </div>
                <div className={styles.fileActions}>
                  <a href={file.file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#021a4a', padding: '2px' }} title="Herunterladen"><FaDownload size={9} /></a>
                  <button onClick={() => handleDeleteAttachment(file.id)} disabled={deletingAttachmentId === file.id} className={styles.commentDeleteBtn} title="Löschen"><FaTrash size={9} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
